import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Tabs,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  MultiSelect,
  Select,
  Paper,
  TextInput,
  Pill,
  Checkbox,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconRefresh,
  IconPackage,
  IconClock,
  IconAlertTriangle,
  IconFileReport,
  IconTruckDelivery,
  IconCircleOff,
  IconCircleCheck,
  IconArrowBack,
  IconSearch,
  IconFilter,
  IconFlask,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { notifications } from "@mantine/notifications";
import {
  usePendingApproval,
  useOverdueSendOuts,
  usePendingShippingSendOuts,
  useRejectedSendOuts,
  useReturnedSendOuts,
  useSentSendOuts,
  useQueueSummary,
  useTissueCypherSendOuts,
  useMarkShipped,
} from "../../hooks/useSendOuts";
import {
  SendOut,
  SendoutStatus,
  SendoutRequestType,
  ExternalInstitutionReason,
} from "../../services/sendOuts.service";
import { useAuthStore } from "../../store";
import SendOutApprovalModal from "../../components/send-outs/SendOutApprovalModal";
import SendOutDetailModal from "../../components/send-outs/SendOutDetailModal";
import OverdueReportModal from "../../components/send-outs/OverdueReportModal";
import LogNewSendOutModal from "../../components/send-outs/LogNewSendOutModal";
import { TissueCypherSendOutTable } from "../../components/send-outs/TissueCypherSendOutTable";
import {
  formatExpectedReturnDate,
  formatSendOutInstantDate,
  formatSendOutInstantDateTime,
  getExpectedReturnDaysLate,
} from "../../components/send-outs/sendOutDatePresentation";

dayjs.extend(relativeTime);

// ==================== CONSTANTS ====================

const ITEMS_PER_PAGE = 20;

const REQUEST_TYPE_OPTIONS = [
  {
    value: SendoutRequestType.PHYSICIAN_REQUEST,
    label: "Ordering Physician Requesting Consultation",
  },
  { value: SendoutRequestType.EXTERNAL_INSTITUTION, label: "External Request" },
  { value: SendoutRequestType.MATERIAL_RETURN, label: "Material Return" },
  {
    value: SendoutRequestType.GCP_CONSULT,
    label: "Pathologist Requires Expert Consultation",
  },
];

const REQUEST_TYPE_SHORT_LABELS: Record<string, string> = {
  [SendoutRequestType.PHYSICIAN_REQUEST]: "Physician Request",
  [SendoutRequestType.EXTERNAL_INSTITUTION]: "External Request",
  [SendoutRequestType.GCP_CONSULT]: "GCP Consult",
  [SendoutRequestType.MATERIAL_RETURN]: "Material Return",
};

const PRIORITY_OPTIONS = [
  { value: "stat", label: "STAT" },
  { value: "urgent", label: "Urgent" },
  { value: "routine", label: "Routine" },
];

const PRIORITY_LABELS: Record<string, string> = {
  stat: "STAT",
  urgent: "Urgent",
  routine: "Routine",
};

const RETURN_STATUS_OPTIONS = [
  { value: SendoutStatus.RECEIVED, label: "Fully Returned" },
  { value: SendoutStatus.RECEIVED_PARTIALLY, label: "Partially Returned" },
];

const RETURN_STATUS_LABELS: Record<string, string> = {
  [SendoutStatus.RECEIVED]: "Fully Returned",
  [SendoutStatus.RECEIVED_PARTIALLY]: "Partially Returned",
};

const REPORT_INCLUDED_OPTIONS = [
  { value: "yes", label: "Report Included" },
  { value: "no", label: "Report Not Included" },
];

const TRACKING_NUMBER_OPTIONS = [
  { value: "yes", label: "Has Tracking Number" },
  { value: "no", label: "No Tracking Number" },
];

const EXTERNAL_REASON_OPTIONS = [
  {
    value: ExternalInstitutionReason.CONTINUATION_OF_CARE,
    label: "Continuation of Care",
  },
  { value: ExternalInstitutionReason.LEGAL_REQUEST, label: "Legal Request" },
  {
    value: ExternalInstitutionReason.OUTSIDE_REQUEST_ADDITIONAL_TESTING,
    label: "Outside Request for Additional Testing",
  },
  { value: ExternalInstitutionReason.OTHER, label: "Other" },
];

const EXTERNAL_REASON_SHORT_LABELS: Record<string, string> = {
  [ExternalInstitutionReason.CONTINUATION_OF_CARE]: "Continuation of Care",
  [ExternalInstitutionReason.LEGAL_REQUEST]: "Legal Request",
  [ExternalInstitutionReason.OUTSIDE_REQUEST_ADDITIONAL_TESTING]:
    "Outside Req. / Add. Testing",
  [ExternalInstitutionReason.OTHER]: "Other",
};

// ==================== TYPES ====================

type TabKey =
  | "tissue-cypher"
  | "pending-approval"
  | "pending-shipping"
  | "sent"
  | "overdue"
  | "rejected"
  | "returned";

const VALID_TABS: TabKey[] = [
  "pending-approval",
  "pending-shipping",
  "tissue-cypher",
  "sent",
  "overdue",
  "rejected",
  "returned",
];

const TAB_TITLES: Record<TabKey, string> = {
  "tissue-cypher": "Tissue Cypher",
  "pending-approval": "Pending Approval",
  "pending-shipping": "Pending Shipping",
  sent: "Sent",
  overdue: "Overdue",
  rejected: "Rejected",
  returned: "Returned",
};

interface TabFilters {
  search: string;
  page: number;
  requestTypes: string[];
  priorities: string[];
  destinations: string[];
  requestedBys: string[];
  orderingInstitutions: string[];
  externalInstitutionReasons: string[];
  reportIncluded: boolean | null;
  hasTrackingNumber: boolean | null;
  returnStatus: string[];
  pendingReviewOnly: boolean;
}

const makeDefaultFilters = (): TabFilters => ({
  search: "",
  page: 1,
  requestTypes: [],
  priorities: [],
  destinations: [],
  requestedBys: [],
  orderingInstitutions: [],
  externalInstitutionReasons: [],
  reportIncluded: null,
  hasTrackingNumber: null,
  returnStatus: [],
  pendingReviewOnly: false,
});

// ==================== HELPERS ====================

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim();
}

function getPriorityBadge(sendOut: SendOut) {
  if (!sendOut.priority) return null;
  const map: Record<string, { color: string; label: string }> = {
    stat: { color: "red", label: "STAT" },
    urgent: { color: "orange", label: "Urgent" },
    routine: { color: "blue", label: "Routine" },
  };
  const p = map[sendOut.priority] ?? { color: "gray", label: sendOut.priority };
  return (
    <Badge color={p.color} variant="filled" size="xs">
      {p.label}
    </Badge>
  );
}

function formatExternalInstitutionReason(sendOut: SendOut): string {
  if (sendOut.externalInstitutionReason) {
    switch (sendOut.externalInstitutionReason) {
      case ExternalInstitutionReason.CONTINUATION_OF_CARE:
        return "Continuation of Care";
      case ExternalInstitutionReason.LEGAL_REQUEST:
        return "Legal Request";
      case ExternalInstitutionReason.OUTSIDE_REQUEST_ADDITIONAL_TESTING:
        return "Outside Request for Additional Testing";
      case ExternalInstitutionReason.OTHER:
        return sendOut.externalInstitutionReasonOther
          ? `Other: ${sendOut.externalInstitutionReasonOther}`
          : "Other";
      default:
        return sendOut.externalInstitutionReason;
    }
  }
  return sendOut.reason || "—";
}

function formatRequestType(type: SendoutRequestType): string {
  switch (type) {
    case SendoutRequestType.PHYSICIAN_REQUEST:
      return "Ordering Physician Requesting Consultation";
    case SendoutRequestType.EXTERNAL_INSTITUTION:
      return "External Request";
    case SendoutRequestType.GCP_CONSULT:
      return "Pathologist Requires Expert Consultation";
    case SendoutRequestType.MATERIAL_RETURN:
      return "Material Return";
    default:
      return type;
  }
}

function getMaterialsSummary(sendOut: SendOut): string {
  const slideCount = sendOut.slidesSent?.length || 0;
  const blockCount = sendOut.blocksSent?.length || 0;
  if (slideCount > 0 && blockCount > 0) {
    return `${slideCount} slide${
      slideCount > 1 ? "s" : ""
    } across ${blockCount} block${blockCount > 1 ? "s" : ""}`;
  }
  if (slideCount > 0) return `${slideCount} slide${slideCount > 1 ? "s" : ""}`;
  if (blockCount > 0) return `${blockCount} block${blockCount > 1 ? "s" : ""}`;
  return "No materials";
}

function formatUserName(user?: { fullName: string } | null): string {
  if (!user) return "N/A";
  return user.fullName;
}

function getDestinationName(sendOut: SendOut): string {
  if (sendOut.externalInstitution?.name)
    return sendOut.externalInstitution.name;
  if (sendOut.stainProvider?.name) return sendOut.stainProvider.name;
  return "N/A";
}

function applyTabFilters(
  sendOuts: SendOut[],
  search: string,
  filters: Omit<TabFilters, "search" | "page">
): SendOut[] {
  let result = sendOuts;

  if (search) {
    const q = search.toLowerCase();
    result = result.filter(
      (s) =>
        s.case?.caseNumber?.toLowerCase().includes(q) ||
        getDestinationName(s).toLowerCase().includes(q) ||
        formatUserName(s.requestedBy).toLowerCase().includes(q) ||
        (s.contactPerson?.toLowerCase().includes(q) ?? false) ||
        (s.trackingNumber?.toLowerCase().includes(q) ?? false)
    );
  }

  if (filters.requestTypes.length > 0) {
    result = result.filter((s) => filters.requestTypes.includes(s.requestType));
  }

  if (filters.priorities.length > 0) {
    result = result.filter(
      (s) => s.priority != null && filters.priorities.includes(s.priority)
    );
  }

  if (filters.destinations.length > 0) {
    result = result.filter((s) =>
      filters.destinations.includes(getDestinationName(s))
    );
  }

  if (filters.requestedBys.length > 0) {
    result = result.filter((s) =>
      filters.requestedBys.includes(formatUserName(s.requestedBy))
    );
  }

  if (filters.orderingInstitutions.length > 0) {
    result = result.filter(
      (s) =>
        s.orderingInstitution?.name != null &&
        filters.orderingInstitutions.includes(s.orderingInstitution.name)
    );
  }

  if (filters.reportIncluded !== null) {
    result = result.filter((s) => s.reportIncluded === filters.reportIncluded);
  }

  if (filters.hasTrackingNumber !== null) {
    result = result.filter((s) =>
      filters.hasTrackingNumber
        ? s.trackingNumber != null && s.trackingNumber !== ""
        : s.trackingNumber == null || s.trackingNumber === ""
    );
  }

  if (filters.externalInstitutionReasons.length > 0) {
    result = result.filter(
      (s) =>
        s.externalInstitutionReason != null &&
        filters.externalInstitutionReasons.includes(s.externalInstitutionReason)
    );
  }

  if (filters.returnStatus.length > 0) {
    result = result.filter((s) => filters.returnStatus.includes(s.status));
  }

  if (filters.pendingReviewOnly) {
    result = result.filter((s) => !!s.isPendingReview);
  }

  return result;
}

// ==================== COMPONENT ====================

export default function SendOutQueuePage() {
  const [searchParams] = useSearchParams();
  const { profile } = useAuthStore();
  const hasApprovePermission =
    profile?.permissions?.includes("approve_send_outs") ?? false;

  // Tab state — driven by ?tab= query param
  const tabParam = searchParams.get("tab") as TabKey | null;
  const highlightId = searchParams.get("highlight");
  const initialTab =
    tabParam && VALID_TABS.includes(tabParam) ? tabParam : "pending-approval";
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  // Modal disclosures
  const [approvalModalOpen, { open: openApproval, close: closeApproval }] =
    useDisclosure(false);
  const [detailModalOpen, { open: openDetail, close: closeDetail }] =
    useDisclosure(false);
  const [
    overdueReportOpen,
    { open: openOverdueReport, close: closeOverdueReport },
  ] = useDisclosure(false);
  const [logNewOpen, { open: openLogNew, close: closeLogNew }] =
    useDisclosure(false);
  const [filterModalOpen, { open: openFilterModal, close: closeFilterModal }] =
    useDisclosure(false);

  const [resubmitSendOut, setResubmitSendOut] = useState<SendOut | null>(null);
  const [selectedSendOutId, setSelectedSendOutId] = useState<string | null>(
    null
  );
  const [tissueCypherToShip, setTissueCypherToShip] = useState<SendOut | null>(
    null
  );
  const markShippedMutation = useMarkShipped();

  // Per-tab filter state (persists when switching tabs)
  const [tabFilters, setTabFilters] = useState<Record<TabKey, TabFilters>>(
    () =>
      Object.fromEntries(
        VALID_TABS.map((t) => [t, makeDefaultFilters()])
      ) as Record<TabKey, TabFilters>
  );
  const [pendingFilters, setPendingFilters] = useState<TabFilters>(
    makeDefaultFilters()
  );

  const currentFilters = tabFilters[activeTab];
  const [debouncedSearch] = useDebouncedValue(currentFilters.search, 300);

  const updateTabFilter = (tab: TabKey, update: Partial<TabFilters>) => {
    setTabFilters((prev) => ({
      ...prev,
      [tab]: { ...prev[tab], ...update },
    }));
  };

  const hasActiveFilters =
    currentFilters.requestTypes.length > 0 ||
    currentFilters.priorities.length > 0 ||
    currentFilters.destinations.length > 0 ||
    currentFilters.requestedBys.length > 0 ||
    currentFilters.orderingInstitutions.length > 0 ||
    currentFilters.externalInstitutionReasons.length > 0 ||
    currentFilters.reportIncluded !== null ||
    currentFilters.hasTrackingNumber !== null ||
    currentFilters.returnStatus.length > 0 ||
    currentFilters.pendingReviewOnly;

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...currentFilters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setTabFilters((prev) => ({
      ...prev,
      [activeTab]: { ...pendingFilters, page: 1 },
    }));
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    setTabFilters((prev) => ({
      ...prev,
      [activeTab]: { ...makeDefaultFilters(), search: prev[activeTab].search },
    }));
  };

  // Queue data
  const {
    data: pendingApproval = [],
    isLoading: loadingPending,
    refetch: refetchPending,
    isFetching: fetchingPending,
  } = usePendingApproval();
  const {
    data: tissueCypher = [],
    isLoading: loadingTissueCypher,
    isError: tissueCypherError,
    refetch: refetchTissueCypher,
    isFetching: fetchingTissueCypher,
  } = useTissueCypherSendOuts();
  const {
    data: pendingShipping = [],
    isLoading: loadingShipping,
    refetch: refetchShipping,
    isFetching: fetchingShipping,
  } = usePendingShippingSendOuts();
  const {
    data: sent = [],
    isLoading: loadingSent,
    refetch: refetchSent,
    isFetching: fetchingSent,
  } = useSentSendOuts();
  const {
    data: overdue = [],
    isLoading: loadingOverdue,
    refetch: refetchOverdue,
    isFetching: fetchingOverdue,
  } = useOverdueSendOuts();
  const {
    data: rejected = [],
    isLoading: loadingRejected,
    refetch: refetchRejected,
  } = useRejectedSendOuts();
  const {
    data: returned = [],
    isLoading: loadingReturned,
    refetch: refetchReturned,
  } = useReturnedSendOuts();

  const { data: summary, refetch: refetchSummary } = useQueueSummary();

  const isFetching =
    fetchingTissueCypher ||
    fetchingPending ||
    fetchingShipping ||
    fetchingSent ||
    fetchingOverdue;

  const handleRefreshAll = () => {
    refetchTissueCypher();
    refetchPending();
    refetchShipping();
    refetchSent();
    refetchOverdue();
    refetchRejected();
    refetchReturned();
    refetchSummary();
  };

  // Filtered + paginated data for the active tab
  const activeRawData = useMemo(() => {
    switch (activeTab) {
      case "tissue-cypher":
        return tissueCypher;
      case "pending-approval":
        return pendingApproval;
      case "pending-shipping":
        return pendingShipping;
      case "sent":
        return sent;
      case "overdue":
        return overdue;
      case "rejected":
        return rejected;
      case "returned":
        return returned;
      default:
        return [];
    }
  }, [
    activeTab,
    tissueCypher,
    pendingApproval,
    pendingShipping,
    sent,
    overdue,
    rejected,
    returned,
  ]);

  // Derived filter options from loaded data (no extra API calls)
  const destinationOptions = useMemo(() => {
    const names = new Set(
      activeRawData.map(getDestinationName).filter((n) => n !== "N/A")
    );
    return Array.from(names)
      .sort()
      .map((n) => ({ value: n, label: n }));
  }, [activeRawData]);

  const requestedByOptions = useMemo(() => {
    const names = new Set(
      activeRawData
        .map((s) => formatUserName(s.requestedBy))
        .filter((n) => n !== "N/A")
    );
    return Array.from(names)
      .sort()
      .map((n) => ({ value: n, label: n }));
  }, [activeRawData]);

  const orderingInstitutionOptions = useMemo(() => {
    const names = new Set(
      activeRawData
        .map((s) => s.orderingInstitution?.name)
        .filter((n): n is string => !!n)
    );
    return Array.from(names)
      .sort()
      .map((n) => ({ value: n, label: n }));
  }, [activeRawData]);

  const filteredData = useMemo(
    () =>
      applyTabFilters(activeRawData, debouncedSearch, {
        requestTypes: currentFilters.requestTypes,
        priorities: currentFilters.priorities,
        destinations: currentFilters.destinations,
        requestedBys: currentFilters.requestedBys,
        orderingInstitutions: currentFilters.orderingInstitutions,
        externalInstitutionReasons: currentFilters.externalInstitutionReasons,
        reportIncluded: currentFilters.reportIncluded,
        hasTrackingNumber: currentFilters.hasTrackingNumber,
        returnStatus: currentFilters.returnStatus,
        pendingReviewOnly: currentFilters.pendingReviewOnly,
      }),
    [
      activeRawData,
      debouncedSearch,
      currentFilters.requestTypes,
      currentFilters.priorities,
      currentFilters.destinations,
      currentFilters.requestedBys,
      currentFilters.orderingInstitutions,
      currentFilters.externalInstitutionReasons,
      currentFilters.reportIncluded,
      currentFilters.hasTrackingNumber,
      currentFilters.returnStatus,
      currentFilters.pendingReviewOnly,
    ]
  );

  const totalPages = useMemo(
    () => Math.ceil(filteredData.length / ITEMS_PER_PAGE),
    [filteredData.length]
  );
  const displayedPage = useMemo(
    () => Math.min(currentFilters.page, Math.max(totalPages, 1)),
    [currentFilters.page, totalPages]
  );
  const pagedData = useMemo(
    () =>
      filteredData.slice(
        (displayedPage - 1) * ITEMS_PER_PAGE,
        displayedPage * ITEMS_PER_PAGE
      ),
    [displayedPage, filteredData]
  );

  // Highlight row support
  const highlightRowRef = useRef<HTMLTableRowElement | null>(null);
  const [highlightActive, setHighlightActive] = useState(false);

  useEffect(() => {
    if (!highlightId) return;
    const timer = setTimeout(() => {
      if (highlightRowRef.current) {
        highlightRowRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setHighlightActive(true);
        setTimeout(() => setHighlightActive(false), 2500);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [highlightId, activeTab, pagedData]);

  const handleApprovalClick = (sendOut: SendOut) => {
    setSelectedSendOutId(sendOut.id);
    openApproval();
  };

  const handleDetailClick = (sendOut: SendOut) => {
    setSelectedSendOutId(sendOut.id);
    openDetail();
  };

  const handleTissueCypherShipClick = useCallback((sendOut: SendOut) => {
    setTissueCypherToShip(sendOut);
  }, []);

  const handleTissueCypherShipClose = useCallback(() => {
    setTissueCypherToShip(null);
  }, []);

  const handleTissueCypherShipConfirm = useCallback(() => {
    if (!tissueCypherToShip) return;
    markShippedMutation.mutate(
      { id: tissueCypherToShip.id },
      {
        onSuccess: () => {
          notifications.show({
            title: "Tissue Cypher shipped",
            message: "The Castle send-out and case were updated together.",
            color: "green",
          });
          handleTissueCypherShipClose();
        },
        onError: () => {
          notifications.show({
            title: "Shipment not recorded",
            message: "Review the send-out status and try again.",
            color: "red",
          });
        },
      }
    );
  }, [handleTissueCypherShipClose, markShippedMutation, tissueCypherToShip]);

  const handleResubmitClick = (sendOut: SendOut) => {
    setResubmitSendOut(sendOut);
    openLogNew();
  };

  const handleLogNewClose = () => {
    setResubmitSendOut(null);
    closeLogNew();
  };

  const handleTabChange = (tab: string | null) => {
    if (tab && VALID_TABS.includes(tab as TabKey)) {
      setActiveTab(tab as TabKey);
      refetchSummary();
    }
  };

  // ==================== BADGE COUNTS ====================

  const counts = useMemo(
    () => ({
      pendingApproval: summary?.pendingApproval ?? pendingApproval.length,
      tissueCypher: summary?.tissueCypher ?? tissueCypher.length,
      pendingShipping: summary?.pendingShipping ?? pendingShipping.length,
      sent: summary?.sent ?? sent.length,
      overdue: summary?.overdue ?? overdue.length,
      rejected: summary?.rejected ?? rejected.length,
      returned: summary?.returned ?? returned.length,
    }),
    [
      summary,
      tissueCypher,
      pendingApproval,
      pendingShipping,
      sent,
      overdue,
      rejected,
      returned,
    ]
  );

  // ==================== ROW HIGHLIGHT STYLE ====================

  const rowStyle = (id: string) =>
    id === highlightId && highlightActive
      ? {
          background: "var(--mantine-color-yellow-1)",
          transition: "background 0.4s",
        }
      : undefined;

  const rowRef = (id: string) =>
    id === highlightId
      ? (el: HTMLTableRowElement | null) => {
          highlightRowRef.current = el;
        }
      : undefined;

  // ==================== FILTER CONTEXT ====================

  const hasPriorityFilter = [
    "pending-approval",
    "pending-shipping",
    "sent",
    "overdue",
  ].includes(activeTab);
  const hasPendingReviewFilter = activeTab === "returned";
  const hasReportIncludedFilter = activeTab === "pending-approval";
  const hasTrackingNumberFilter =
    activeTab === "sent" || activeTab === "overdue";
  const hasReturnStatusFilter = activeTab === "returned";

  // ==================== TABLE SECTION HELPERS ====================

  const tablePaginationHeader = (
    <Group justify="space-between">
      <Text fw={500} size="lg">
        {TAB_TITLES[activeTab]} ({filteredData.length})
      </Text>
      {totalPages > 1 && (
        <Group gap="md">
          <Text size="sm" c="dimmed">
            Showing {(displayedPage - 1) * ITEMS_PER_PAGE + 1}–
            {Math.min(displayedPage * ITEMS_PER_PAGE, filteredData.length)} of{" "}
            {filteredData.length}
          </Text>
          <Pagination
            total={totalPages}
            value={displayedPage}
            onChange={(p) => updateTabFilter(activeTab, { page: p })}
            size="sm"
          />
        </Group>
      )}
    </Group>
  );

  const emptyState = (rawCount: number) => (
    <Text c="dimmed" ta="center" py="xl">
      {rawCount === 0
        ? `No ${TAB_TITLES[activeTab].toLowerCase()} send-outs`
        : "No results match the current search or filters"}
    </Text>
  );

  const loadingState = (
    <Center p="xl">
      <Loader />
    </Center>
  );

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* ===== HEADER ===== */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="sm">
                <IconPackage size={28} />
                <Title order={2}>Send-Outs Queue</Title>
              </Group>
              <Text size="sm" c="dimmed" mt="xs">
                Approve external opinion requests, track shipped materials, and
                log receipt of returned materials and reports
              </Text>
            </div>
            <Group>
              {counts.overdue > 0 && (
                <Button
                  leftSection={<IconFileReport size={16} />}
                  variant="light"
                  color="red"
                  onClick={openOverdueReport}
                >
                  Overdue Report ({counts.overdue})
                </Button>
              )}
              <Button
                onClick={() => {
                  setResubmitSendOut(null);
                  openLogNew();
                }}
              >
                Log New Send-Out
              </Button>
            </Group>
          </Group>

          {/* ===== SEARCH AND FILTERS ROW ===== */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by case #, destination, requester..."
                  value={currentFilters.search}
                  onChange={(e) => {
                    updateTabFilter(activeTab, {
                      search: e.target.value,
                      page: 1,
                    });
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={handleRefreshAll}
                    loading={isFetching}
                    title="Refresh"
                  >
                    <IconRefresh size={16} />
                  </ActionIcon>
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" align="center" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {currentFilters.requestTypes.map((rt) => (
                      <Pill
                        key={rt}
                        withRemoveButton
                        onRemove={() =>
                          updateTabFilter(activeTab, {
                            requestTypes: currentFilters.requestTypes.filter(
                              (x) => x !== rt
                            ),
                            page: 1,
                          })
                        }
                      >
                        {REQUEST_TYPE_SHORT_LABELS[rt] || rt}
                      </Pill>
                    ))}
                    {currentFilters.priorities.map((p) => (
                      <Pill
                        key={p}
                        withRemoveButton
                        onRemove={() =>
                          updateTabFilter(activeTab, {
                            priorities: currentFilters.priorities.filter(
                              (x) => x !== p
                            ),
                            page: 1,
                          })
                        }
                      >
                        {PRIORITY_LABELS[p] || p}
                      </Pill>
                    ))}
                    {currentFilters.destinations.map((d) => (
                      <Pill
                        key={d}
                        withRemoveButton
                        onRemove={() =>
                          updateTabFilter(activeTab, {
                            destinations: currentFilters.destinations.filter(
                              (x) => x !== d
                            ),
                            page: 1,
                          })
                        }
                      >
                        {d}
                      </Pill>
                    ))}
                    {currentFilters.requestedBys.map((r) => (
                      <Pill
                        key={r}
                        withRemoveButton
                        onRemove={() =>
                          updateTabFilter(activeTab, {
                            requestedBys: currentFilters.requestedBys.filter(
                              (x) => x !== r
                            ),
                            page: 1,
                          })
                        }
                      >
                        {r}
                      </Pill>
                    ))}
                    {currentFilters.orderingInstitutions.map((o) => (
                      <Pill
                        key={o}
                        withRemoveButton
                        onRemove={() =>
                          updateTabFilter(activeTab, {
                            orderingInstitutions:
                              currentFilters.orderingInstitutions.filter(
                                (x) => x !== o
                              ),
                            page: 1,
                          })
                        }
                      >
                        {o}
                      </Pill>
                    ))}
                    {currentFilters.externalInstitutionReasons.map((r) => (
                      <Pill
                        key={r}
                        withRemoveButton
                        onRemove={() =>
                          updateTabFilter(activeTab, {
                            externalInstitutionReasons:
                              currentFilters.externalInstitutionReasons.filter(
                                (x) => x !== r
                              ),
                            page: 1,
                          })
                        }
                      >
                        {EXTERNAL_REASON_SHORT_LABELS[r] || r}
                      </Pill>
                    ))}
                    {currentFilters.reportIncluded !== null && (
                      <Pill
                        withRemoveButton
                        onRemove={() =>
                          updateTabFilter(activeTab, {
                            reportIncluded: null,
                            page: 1,
                          })
                        }
                      >
                        {currentFilters.reportIncluded
                          ? "Report Included"
                          : "No Report"}
                      </Pill>
                    )}
                    {currentFilters.hasTrackingNumber !== null && (
                      <Pill
                        withRemoveButton
                        onRemove={() =>
                          updateTabFilter(activeTab, {
                            hasTrackingNumber: null,
                            page: 1,
                          })
                        }
                      >
                        {currentFilters.hasTrackingNumber
                          ? "Has Tracking #"
                          : "No Tracking #"}
                      </Pill>
                    )}
                    {currentFilters.returnStatus.map((rs) => (
                      <Pill
                        key={rs}
                        withRemoveButton
                        onRemove={() =>
                          updateTabFilter(activeTab, {
                            returnStatus: currentFilters.returnStatus.filter(
                              (x) => x !== rs
                            ),
                            page: 1,
                          })
                        }
                      >
                        {RETURN_STATUS_LABELS[rs] || rs}
                      </Pill>
                    ))}
                    {currentFilters.pendingReviewOnly && (
                      <Pill
                        withRemoveButton
                        onRemove={() =>
                          updateTabFilter(activeTab, {
                            pendingReviewOnly: false,
                            page: 1,
                          })
                        }
                      >
                        Pending Review
                      </Pill>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  Showing {filteredData.length} of {activeRawData.length} items
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* ===== SEND-OUT QUEUE TABS ===== */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            keepMounted={false}
          >
            <Tabs.List>
              <Tabs.Tab
                value="pending-approval"
                leftSection={<IconClock size={14} />}
                rightSection={
                  counts.pendingApproval > 0 ? (
                    <Badge size="xs" color="orange" variant="filled">
                      {counts.pendingApproval}
                    </Badge>
                  ) : undefined
                }
              >
                Pending Approval
              </Tabs.Tab>

              <Tabs.Tab
                value="pending-shipping"
                leftSection={<IconTruckDelivery size={14} />}
                rightSection={
                  counts.pendingShipping > 0 ? (
                    <Badge size="xs" color="yellow" variant="filled">
                      {counts.pendingShipping}
                    </Badge>
                  ) : undefined
                }
              >
                Pending Shipping
              </Tabs.Tab>

              <Tabs.Tab
                value="tissue-cypher"
                leftSection={<IconFlask size={14} />}
                rightSection={
                  counts.tissueCypher > 0 ? (
                    <Badge size="xs" color="violet" variant="filled">
                      {counts.tissueCypher}
                    </Badge>
                  ) : undefined
                }
              >
                Tissue Cypher
              </Tabs.Tab>

              <Tabs.Tab
                value="sent"
                leftSection={<IconCircleCheck size={14} />}
                rightSection={
                  counts.sent > 0 ? (
                    <Badge size="xs" color="blue" variant="filled">
                      {counts.sent}
                    </Badge>
                  ) : undefined
                }
              >
                Sent
              </Tabs.Tab>

              <Tabs.Tab
                value="overdue"
                leftSection={<IconAlertTriangle size={14} />}
                rightSection={
                  counts.overdue > 0 ? (
                    <Badge size="xs" color="red" variant="filled">
                      {counts.overdue}
                    </Badge>
                  ) : undefined
                }
              >
                Overdue
              </Tabs.Tab>

              <Tabs.Tab
                value="rejected"
                leftSection={<IconCircleOff size={14} />}
                rightSection={
                  counts.rejected > 0 ? (
                    <Badge size="xs" color="red" variant="light">
                      {counts.rejected}
                    </Badge>
                  ) : undefined
                }
              >
                Rejected
              </Tabs.Tab>

              <Tabs.Tab
                value="returned"
                leftSection={<IconArrowBack size={14} />}
                rightSection={
                  counts.returned > 0 ? (
                    <Badge size="xs" color="green" variant="filled">
                      {counts.returned}
                    </Badge>
                  ) : undefined
                }
              >
                Returned
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="tissue-cypher" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  {tablePaginationHeader}
                  <TissueCypherSendOutTable
                    sendOuts={pagedData}
                    isLoading={loadingTissueCypher}
                    hasError={tissueCypherError}
                    emptyMessage={
                      tissueCypher.length === 0
                        ? "No Tissue Cypher materials are ready for send-out"
                        : "No Tissue Cypher send-outs match the current search or filters"
                    }
                    onView={handleDetailClick}
                    onShip={handleTissueCypherShipClick}
                  />
                </Stack>
              </Paper>
            </Tabs.Panel>

            {/* ===== PENDING APPROVAL ===== */}
            <Tabs.Panel value="pending-approval" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  {tablePaginationHeader}
                  {loadingPending ? (
                    loadingState
                  ) : filteredData.length === 0 ? (
                    emptyState(pendingApproval.length)
                  ) : (
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Case #</Table.Th>
                          <Table.Th>Type / Reason</Table.Th>
                          <Table.Th>Ordering → Destination</Table.Th>
                          <Table.Th>Contact Person</Table.Th>
                          <Table.Th>Requested By</Table.Th>
                          <Table.Th>Materials</Table.Th>
                          <Table.Th>Remarks</Table.Th>
                          <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {pagedData.map((sendOut) => (
                          <Table.Tr
                            key={sendOut.id}
                            style={rowStyle(sendOut.id)}
                            ref={rowRef(sendOut.id)}
                          >
                            <Table.Td
                              style={{ minWidth: 120, whiteSpace: "nowrap" }}
                            >
                              <Text fw={500}>
                                {sendOut.case?.caseNumber || "N/A"}
                              </Text>
                              {getPriorityBadge(sendOut)}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {formatRequestType(sendOut.requestType)}
                              </Text>
                              {formatExternalInstitutionReason(sendOut) !==
                                "—" && (
                                <Text
                                  size="xs"
                                  c="dimmed"
                                  lineClamp={2}
                                  style={{ maxWidth: 220 }}
                                >
                                  {stripHtml(
                                    formatExternalInstitutionReason(sendOut)
                                  )}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {sendOut.orderingInstitution?.name ||
                                  sendOut.case?.facility?.name ||
                                  "—"}
                              </Text>
                              <Text size="xs" c="dimmed">
                                →
                              </Text>
                              <Text size="sm">
                                {getDestinationName(sendOut)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              {sendOut.contactPerson ? (
                                <>
                                  <Text size="sm">{sendOut.contactPerson}</Text>
                                  {sendOut.contactPhoneFax && (
                                    <Text size="xs" c="dimmed">
                                      {sendOut.contactPhoneFax}
                                    </Text>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {formatUserName(sendOut.requestedBy)}
                              </Text>
                              <Tooltip
                                label={formatSendOutInstantDateTime(
                                  sendOut.createdAt
                                )}
                              >
                                <Text size="xs" c="dimmed">
                                  {dayjs(sendOut.createdAt).fromNow()}
                                </Text>
                              </Tooltip>
                            </Table.Td>
                            <Table.Td>{getMaterialsSummary(sendOut)}</Table.Td>
                            <Table.Td>
                              <Text
                                size="xs"
                                lineClamp={2}
                                title={sendOut.notes || ""}
                              >
                                {sendOut.notes || "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              {hasApprovePermission ? (
                                <Button
                                  size="xs"
                                  onClick={() => handleApprovalClick(sendOut)}
                                >
                                  Review
                                </Button>
                              ) : (
                                <Button
                                  size="xs"
                                  variant="subtle"
                                  onClick={() => handleDetailClick(sendOut)}
                                >
                                  View
                                </Button>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Stack>
              </Paper>
            </Tabs.Panel>

            {/* ===== PENDING SHIPPING ===== */}
            <Tabs.Panel value="pending-shipping" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  {tablePaginationHeader}
                  {loadingShipping ? (
                    loadingState
                  ) : filteredData.length === 0 ? (
                    emptyState(pendingShipping.length)
                  ) : (
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Case #</Table.Th>
                          <Table.Th>Type / Reason</Table.Th>
                          <Table.Th>Ordering → Destination</Table.Th>
                          <Table.Th>Contact Person</Table.Th>
                          <Table.Th>Requested By</Table.Th>
                          <Table.Th>Materials</Table.Th>
                          <Table.Th>Approved By</Table.Th>
                          <Table.Th>Remarks</Table.Th>
                          <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {pagedData.map((sendOut) => (
                          <Table.Tr
                            key={sendOut.id}
                            style={rowStyle(sendOut.id)}
                            ref={rowRef(sendOut.id)}
                          >
                            <Table.Td
                              style={{ minWidth: 120, whiteSpace: "nowrap" }}
                            >
                              <Text fw={500}>
                                {sendOut.case?.caseNumber || "N/A"}
                              </Text>
                              {getPriorityBadge(sendOut)}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {formatRequestType(sendOut.requestType)}
                              </Text>
                              {formatExternalInstitutionReason(sendOut) !==
                                "—" && (
                                <Text
                                  size="xs"
                                  c="dimmed"
                                  lineClamp={2}
                                  style={{ maxWidth: 220 }}
                                >
                                  {stripHtml(
                                    formatExternalInstitutionReason(sendOut)
                                  )}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {sendOut.orderingInstitution?.name ||
                                  sendOut.case?.facility?.name ||
                                  "—"}
                              </Text>
                              <Text size="xs" c="dimmed">
                                →
                              </Text>
                              <Text size="sm">
                                {getDestinationName(sendOut)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              {sendOut.contactPerson ? (
                                <>
                                  <Text size="sm">{sendOut.contactPerson}</Text>
                                  {sendOut.contactPhoneFax && (
                                    <Text size="xs" c="dimmed">
                                      {sendOut.contactPhoneFax}
                                    </Text>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {formatUserName(sendOut.requestedBy)}
                              </Text>
                              <Tooltip
                                label={formatSendOutInstantDateTime(
                                  sendOut.createdAt
                                )}
                              >
                                <Text size="xs" c="dimmed">
                                  {dayjs(sendOut.createdAt).fromNow()}
                                </Text>
                              </Tooltip>
                            </Table.Td>
                            <Table.Td>{getMaterialsSummary(sendOut)}</Table.Td>
                            <Table.Td>
                              {sendOut.approvedBy ? (
                                <>
                                  <Text size="sm">
                                    {formatUserName(sendOut.approvedBy)}
                                  </Text>
                                  {sendOut.approvedAt && (
                                    <Tooltip
                                      label={formatSendOutInstantDateTime(
                                        sendOut.approvedAt
                                      )}
                                    >
                                      <Text size="xs" c="dimmed">
                                        {dayjs(sendOut.approvedAt).fromNow()}
                                      </Text>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text
                                size="xs"
                                lineClamp={2}
                                title={sendOut.notes || ""}
                              >
                                {sendOut.notes || "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Button
                                size="xs"
                                variant="subtle"
                                onClick={() => handleDetailClick(sendOut)}
                              >
                                View
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Stack>
              </Paper>
            </Tabs.Panel>

            {/* ===== SENT ===== */}
            <Tabs.Panel value="sent" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  {tablePaginationHeader}
                  {loadingSent ? (
                    loadingState
                  ) : filteredData.length === 0 ? (
                    emptyState(sent.length)
                  ) : (
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Case #</Table.Th>
                          <Table.Th>Type / Reason</Table.Th>
                          <Table.Th>Ordering → Destination</Table.Th>
                          <Table.Th>Contact Person</Table.Th>
                          <Table.Th>Requested By</Table.Th>
                          <Table.Th>Materials</Table.Th>
                          <Table.Th>Approved By</Table.Th>
                          <Table.Th>Shipped By</Table.Th>
                          <Table.Th>Remarks</Table.Th>
                          <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {pagedData.map((sendOut) => (
                          <Table.Tr
                            key={sendOut.id}
                            style={rowStyle(sendOut.id)}
                            ref={rowRef(sendOut.id)}
                          >
                            <Table.Td
                              style={{ minWidth: 120, whiteSpace: "nowrap" }}
                            >
                              <Text fw={500}>
                                {sendOut.case?.caseNumber || "N/A"}
                              </Text>
                              {getPriorityBadge(sendOut)}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {formatRequestType(sendOut.requestType)}
                              </Text>
                              {formatExternalInstitutionReason(sendOut) !==
                                "—" && (
                                <Text
                                  size="xs"
                                  c="dimmed"
                                  lineClamp={2}
                                  style={{ maxWidth: 220 }}
                                >
                                  {stripHtml(
                                    formatExternalInstitutionReason(sendOut)
                                  )}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {sendOut.orderingInstitution?.name ||
                                  sendOut.case?.facility?.name ||
                                  "—"}
                              </Text>
                              <Text size="xs" c="dimmed">
                                →
                              </Text>
                              <Text size="sm">
                                {getDestinationName(sendOut)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              {sendOut.contactPerson ? (
                                <>
                                  <Text size="sm">{sendOut.contactPerson}</Text>
                                  {sendOut.contactPhoneFax && (
                                    <Text size="xs" c="dimmed">
                                      {sendOut.contactPhoneFax}
                                    </Text>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {formatUserName(sendOut.requestedBy)}
                              </Text>
                              <Tooltip
                                label={formatSendOutInstantDateTime(
                                  sendOut.createdAt
                                )}
                              >
                                <Text size="xs" c="dimmed">
                                  {dayjs(sendOut.createdAt).fromNow()}
                                </Text>
                              </Tooltip>
                            </Table.Td>
                            <Table.Td>{getMaterialsSummary(sendOut)}</Table.Td>
                            <Table.Td>
                              {sendOut.approvedBy ? (
                                <>
                                  <Text size="sm">
                                    {formatUserName(sendOut.approvedBy)}
                                  </Text>
                                  {sendOut.approvedAt && (
                                    <Tooltip
                                      label={formatSendOutInstantDateTime(
                                        sendOut.approvedAt
                                      )}
                                    >
                                      <Text size="xs" c="dimmed">
                                        {dayjs(sendOut.approvedAt).fromNow()}
                                      </Text>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {sendOut.shippedBy ? (
                                <>
                                  <Text size="sm">
                                    {formatUserName(sendOut.shippedBy)}
                                  </Text>
                                  {sendOut.shippedAt && (
                                    <Tooltip
                                      label={formatSendOutInstantDateTime(
                                        sendOut.shippedAt
                                      )}
                                    >
                                      <Text size="xs" c="dimmed">
                                        {dayjs(sendOut.shippedAt).fromNow()}
                                      </Text>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text
                                size="xs"
                                lineClamp={2}
                                title={sendOut.notes || ""}
                              >
                                {sendOut.notes || "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Button
                                size="xs"
                                variant="subtle"
                                onClick={() => handleDetailClick(sendOut)}
                              >
                                View
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Stack>
              </Paper>
            </Tabs.Panel>

            {/* ===== OVERDUE ===== */}
            <Tabs.Panel value="overdue" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  {tablePaginationHeader}
                  {loadingOverdue ? (
                    loadingState
                  ) : filteredData.length === 0 ? (
                    emptyState(overdue.length)
                  ) : (
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Case #</Table.Th>
                          <Table.Th>Type / Reason</Table.Th>
                          <Table.Th>Ordering → Destination</Table.Th>
                          <Table.Th>Contact Person</Table.Th>
                          <Table.Th>Requested By</Table.Th>
                          <Table.Th>Materials</Table.Th>
                          <Table.Th>Approved By</Table.Th>
                          <Table.Th>Shipped By</Table.Th>
                          <Table.Th>Remarks</Table.Th>
                          <Table.Th>Expected Return</Table.Th>
                          <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {pagedData.map((sendOut) => {
                          const daysLate = sendOut.expectedReturnDate
                            ? getExpectedReturnDaysLate(
                                sendOut.expectedReturnDate
                              ) ?? 0
                            : 0;
                          return (
                            <Table.Tr
                              key={sendOut.id}
                              style={rowStyle(sendOut.id)}
                              ref={rowRef(sendOut.id)}
                            >
                              <Table.Td
                                style={{ minWidth: 120, whiteSpace: "nowrap" }}
                              >
                                <Text fw={500}>
                                  {sendOut.case?.caseNumber || "N/A"}
                                </Text>
                                {getPriorityBadge(sendOut)}
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {formatRequestType(sendOut.requestType)}
                                </Text>
                                {formatExternalInstitutionReason(sendOut) !==
                                  "—" && (
                                  <Text
                                    size="xs"
                                    c="dimmed"
                                    lineClamp={2}
                                    style={{ maxWidth: 220 }}
                                  >
                                    {stripHtml(
                                      formatExternalInstitutionReason(sendOut)
                                    )}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {sendOut.orderingInstitution?.name ||
                                    sendOut.case?.facility?.name ||
                                    "—"}
                                </Text>
                                <Text size="xs" c="dimmed">
                                  →
                                </Text>
                                <Text size="sm">
                                  {getDestinationName(sendOut)}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                {sendOut.contactPerson ? (
                                  <>
                                    <Text size="sm">
                                      {sendOut.contactPerson}
                                    </Text>
                                    {sendOut.contactPhoneFax && (
                                      <Text size="xs" c="dimmed">
                                        {sendOut.contactPhoneFax}
                                      </Text>
                                    )}
                                  </>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    —
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {formatUserName(sendOut.requestedBy)}
                                </Text>
                                <Tooltip
                                  label={formatSendOutInstantDateTime(
                                    sendOut.createdAt
                                  )}
                                >
                                  <Text size="xs" c="dimmed">
                                    {dayjs(sendOut.createdAt).fromNow()}
                                  </Text>
                                </Tooltip>
                              </Table.Td>
                              <Table.Td>
                                {getMaterialsSummary(sendOut)}
                              </Table.Td>
                              <Table.Td>
                                {sendOut.approvedBy ? (
                                  <>
                                    <Text size="sm">
                                      {formatUserName(sendOut.approvedBy)}
                                    </Text>
                                    {sendOut.approvedAt && (
                                      <Tooltip
                                        label={formatSendOutInstantDateTime(
                                          sendOut.approvedAt
                                        )}
                                      >
                                        <Text size="xs" c="dimmed">
                                          {dayjs(sendOut.approvedAt).fromNow()}
                                        </Text>
                                      </Tooltip>
                                    )}
                                  </>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    —
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                {sendOut.shippedBy ? (
                                  <>
                                    <Text size="sm">
                                      {formatUserName(sendOut.shippedBy)}
                                    </Text>
                                    {sendOut.shippedAt && (
                                      <Tooltip
                                        label={formatSendOutInstantDateTime(
                                          sendOut.shippedAt
                                        )}
                                      >
                                        <Text size="xs" c="dimmed">
                                          {dayjs(sendOut.shippedAt).fromNow()}
                                        </Text>
                                      </Tooltip>
                                    )}
                                  </>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    —
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Text
                                  size="xs"
                                  lineClamp={2}
                                  title={sendOut.notes || ""}
                                >
                                  {sendOut.notes || "—"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                {sendOut.expectedReturnDate ? (
                                  <>
                                    <Text size="sm">
                                      {formatExpectedReturnDate(
                                        sendOut.expectedReturnDate
                                      )}
                                    </Text>
                                    <Badge
                                      color="red"
                                      variant="filled"
                                      size="xs"
                                      mt={2}
                                    >
                                      {daysLate}d late
                                    </Badge>
                                  </>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    —
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Button
                                  size="xs"
                                  variant="subtle"
                                  onClick={() => handleDetailClick(sendOut)}
                                >
                                  View
                                </Button>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  )}
                </Stack>
              </Paper>
            </Tabs.Panel>

            {/* ===== REJECTED ===== */}
            <Tabs.Panel value="rejected" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  {tablePaginationHeader}
                  {loadingRejected ? (
                    loadingState
                  ) : filteredData.length === 0 ? (
                    emptyState(rejected.length)
                  ) : (
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Case #</Table.Th>
                          <Table.Th>Type / Reason</Table.Th>
                          <Table.Th>Ordering → Destination</Table.Th>
                          <Table.Th>Requested By</Table.Th>
                          <Table.Th>Approved By</Table.Th>
                          <Table.Th>Shipped By</Table.Th>
                          <Table.Th>Remarks</Table.Th>
                          <Table.Th>Rejected By / On</Table.Th>
                          <Table.Th>Rejection Reason</Table.Th>
                          <Table.Th style={{ width: 130 }}>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {pagedData.map((sendOut) => (
                          <Table.Tr
                            key={sendOut.id}
                            style={rowStyle(sendOut.id)}
                            ref={rowRef(sendOut.id)}
                          >
                            <Table.Td
                              style={{ minWidth: 120, whiteSpace: "nowrap" }}
                            >
                              <Text fw={500}>
                                {sendOut.case?.caseNumber || "N/A"}
                              </Text>
                              {getPriorityBadge(sendOut)}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {formatRequestType(sendOut.requestType)}
                              </Text>
                              {formatExternalInstitutionReason(sendOut) !==
                                "—" && (
                                <Text
                                  size="xs"
                                  c="dimmed"
                                  lineClamp={2}
                                  style={{ maxWidth: 220 }}
                                >
                                  {stripHtml(
                                    formatExternalInstitutionReason(sendOut)
                                  )}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {sendOut.orderingInstitution?.name ||
                                  sendOut.case?.facility?.name ||
                                  "—"}
                              </Text>
                              <Text size="xs" c="dimmed">
                                →
                              </Text>
                              <Text size="sm">
                                {getDestinationName(sendOut)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {formatUserName(sendOut.requestedBy)}
                              </Text>
                              <Tooltip
                                label={formatSendOutInstantDateTime(
                                  sendOut.createdAt
                                )}
                              >
                                <Text size="xs" c="dimmed">
                                  {dayjs(sendOut.createdAt).fromNow()}
                                </Text>
                              </Tooltip>
                            </Table.Td>
                            <Table.Td>
                              {sendOut.approvedBy ? (
                                <>
                                  <Text size="sm">
                                    {formatUserName(sendOut.approvedBy)}
                                  </Text>
                                  {sendOut.approvedAt && (
                                    <Tooltip
                                      label={formatSendOutInstantDateTime(
                                        sendOut.approvedAt
                                      )}
                                    >
                                      <Text size="xs" c="dimmed">
                                        {dayjs(sendOut.approvedAt).fromNow()}
                                      </Text>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {sendOut.shippedBy ? (
                                <>
                                  <Text size="sm">
                                    {formatUserName(sendOut.shippedBy)}
                                  </Text>
                                  {sendOut.shippedAt && (
                                    <Tooltip
                                      label={formatSendOutInstantDateTime(
                                        sendOut.shippedAt
                                      )}
                                    >
                                      <Text size="xs" c="dimmed">
                                        {dayjs(sendOut.shippedAt).fromNow()}
                                      </Text>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text
                                size="xs"
                                lineClamp={2}
                                title={sendOut.notes || ""}
                              >
                                {sendOut.notes || "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              {sendOut.rejectedBy && (
                                <Text size="sm">
                                  {formatUserName(sendOut.rejectedBy)}
                                </Text>
                              )}
                              {sendOut.rejectedAt ? (
                                <Tooltip
                                  label={formatSendOutInstantDateTime(
                                    sendOut.rejectedAt
                                  )}
                                >
                                  <Text size="xs" c="dimmed">
                                    {formatSendOutInstantDate(
                                      sendOut.rejectedAt
                                    )}
                                  </Text>
                                </Tooltip>
                              ) : (
                                <Tooltip
                                  label={formatSendOutInstantDateTime(
                                    sendOut.updatedAt
                                  )}
                                >
                                  <Text size="xs" c="dimmed">
                                    {formatSendOutInstantDate(
                                      sendOut.updatedAt
                                    )}
                                  </Text>
                                </Tooltip>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text
                                size="sm"
                                c="red"
                                lineClamp={2}
                                style={{ maxWidth: 260 }}
                              >
                                {sendOut.rejectionReason ||
                                  "No reason provided"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Button
                                size="xs"
                                variant="light"
                                color="orange"
                                onClick={() => handleResubmitClick(sendOut)}
                              >
                                Edit &amp; Resubmit
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Stack>
              </Paper>
            </Tabs.Panel>

            {/* ===== RETURNED ===== */}
            <Tabs.Panel value="returned" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  {tablePaginationHeader}
                  {loadingReturned ? (
                    loadingState
                  ) : filteredData.length === 0 ? (
                    emptyState(returned.length)
                  ) : (
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Case #</Table.Th>
                          <Table.Th>Type / Reason</Table.Th>
                          <Table.Th>Ordering → Destination</Table.Th>
                          <Table.Th>Requested By</Table.Th>
                          <Table.Th>Approved By</Table.Th>
                          <Table.Th>Shipped By</Table.Th>
                          <Table.Th>Received By</Table.Th>
                          <Table.Th>Remarks</Table.Th>
                          <Table.Th>Return Status</Table.Th>
                          <Table.Th style={{ width: 180 }}>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {pagedData.map((sendOut) => (
                          <Table.Tr
                            key={sendOut.id}
                            style={rowStyle(sendOut.id)}
                            ref={rowRef(sendOut.id)}
                          >
                            <Table.Td
                              style={{ minWidth: 120, whiteSpace: "nowrap" }}
                            >
                              <Text fw={500}>
                                {sendOut.case?.caseNumber || "N/A"}
                              </Text>
                              {getPriorityBadge(sendOut)}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {formatRequestType(sendOut.requestType)}
                              </Text>
                              {formatExternalInstitutionReason(sendOut) !==
                                "—" && (
                                <Text
                                  size="xs"
                                  c="dimmed"
                                  lineClamp={2}
                                  style={{ maxWidth: 220 }}
                                >
                                  {stripHtml(
                                    formatExternalInstitutionReason(sendOut)
                                  )}
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {sendOut.orderingInstitution?.name ||
                                  sendOut.case?.facility?.name ||
                                  "—"}
                              </Text>
                              <Text size="xs" c="dimmed">
                                →
                              </Text>
                              <Text size="sm">
                                {getDestinationName(sendOut)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {formatUserName(sendOut.requestedBy)}
                              </Text>
                              <Tooltip
                                label={formatSendOutInstantDateTime(
                                  sendOut.createdAt
                                )}
                              >
                                <Text size="xs" c="dimmed">
                                  {dayjs(sendOut.createdAt).fromNow()}
                                </Text>
                              </Tooltip>
                            </Table.Td>
                            <Table.Td>
                              {sendOut.approvedBy ? (
                                <>
                                  <Text size="sm">
                                    {formatUserName(sendOut.approvedBy)}
                                  </Text>
                                  {sendOut.approvedAt && (
                                    <Tooltip
                                      label={formatSendOutInstantDateTime(
                                        sendOut.approvedAt
                                      )}
                                    >
                                      <Text size="xs" c="dimmed">
                                        {dayjs(sendOut.approvedAt).fromNow()}
                                      </Text>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {sendOut.shippedBy ? (
                                <>
                                  <Text size="sm">
                                    {formatUserName(sendOut.shippedBy)}
                                  </Text>
                                  {sendOut.shippedAt && (
                                    <Tooltip
                                      label={formatSendOutInstantDateTime(
                                        sendOut.shippedAt
                                      )}
                                    >
                                      <Text size="xs" c="dimmed">
                                        {dayjs(sendOut.shippedAt).fromNow()}
                                      </Text>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {sendOut.receivedBy ? (
                                <>
                                  <Text size="sm">
                                    {formatUserName(sendOut.receivedBy)}
                                  </Text>
                                  {sendOut.receivedAt && (
                                    <Tooltip
                                      label={formatSendOutInstantDateTime(
                                        sendOut.receivedAt
                                      )}
                                    >
                                      <Text size="xs" c="dimmed">
                                        {dayjs(sendOut.receivedAt).fromNow()}
                                      </Text>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text
                                size="xs"
                                lineClamp={2}
                                title={sendOut.notes || ""}
                              >
                                {sendOut.notes || "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              {sendOut.status ===
                              SendoutStatus.RECEIVED_PARTIALLY ? (
                                <Badge size="sm" color="cyan" variant="light">
                                  Partially Received
                                </Badge>
                              ) : (
                                <Badge size="sm" color="green" variant="light">
                                  Fully Received
                                </Badge>
                              )}
                              {sendOut.isPendingReview ? (
                                <Tooltip
                                  label={
                                    sendOut.qaReviewer
                                      ? `Will be reviewed by ${sendOut.qaReviewer.fullName}`
                                      : "Awaiting QA reviewer"
                                  }
                                  withArrow
                                >
                                  <Badge
                                    size="sm"
                                    color="yellow"
                                    variant="filled"
                                    mt={4}
                                    display="block"
                                    style={{ cursor: "default" }}
                                  >
                                    Pending QA Review
                                  </Badge>
                                </Tooltip>
                              ) : sendOut.qaReviewer ? (
                                <Text size="xs" c="dimmed" mt={4}>
                                  QA: {sendOut.qaReviewer.fullName}
                                </Text>
                              ) : null}
                            </Table.Td>
                            <Table.Td>
                              <Button
                                size="xs"
                                variant="subtle"
                                onClick={() => handleDetailClick(sendOut)}
                              >
                                View
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Stack>
              </Paper>
            </Tabs.Panel>
          </Tabs>
        </Stack>

        {/* ===== FILTER MODAL ===== */}
        <Modal
          opened={filterModalOpen}
          onClose={closeFilterModal}
          title="Filter Send-Outs"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <MultiSelect
              label="Request Type"
              placeholder="All types"
              data={REQUEST_TYPE_OPTIONS}
              value={pendingFilters.requestTypes}
              onChange={(v) =>
                setPendingFilters((prev) => ({ ...prev, requestTypes: v }))
              }
              clearable
            />
            {hasPriorityFilter && (
              <MultiSelect
                label="Priority"
                placeholder="All priorities"
                data={PRIORITY_OPTIONS}
                value={pendingFilters.priorities}
                onChange={(v) =>
                  setPendingFilters((prev) => ({ ...prev, priorities: v }))
                }
                clearable
              />
            )}
            <MultiSelect
              label="Destination"
              placeholder="All destinations"
              data={destinationOptions}
              value={pendingFilters.destinations}
              onChange={(v) =>
                setPendingFilters((prev) => ({ ...prev, destinations: v }))
              }
              searchable
              clearable
            />
            <MultiSelect
              label="Requested By"
              placeholder="All requesters"
              data={requestedByOptions}
              value={pendingFilters.requestedBys}
              onChange={(v) =>
                setPendingFilters((prev) => ({ ...prev, requestedBys: v }))
              }
              searchable
              clearable
            />
            {orderingInstitutionOptions.length > 0 && (
              <MultiSelect
                label="Ordering Institution"
                placeholder="All institutions"
                data={orderingInstitutionOptions}
                value={pendingFilters.orderingInstitutions}
                onChange={(v) =>
                  setPendingFilters((prev) => ({
                    ...prev,
                    orderingInstitutions: v,
                  }))
                }
                searchable
                clearable
              />
            )}
            <MultiSelect
              label="External Request Reason"
              placeholder="All reasons"
              data={EXTERNAL_REASON_OPTIONS}
              value={pendingFilters.externalInstitutionReasons}
              onChange={(v) =>
                setPendingFilters((prev) => ({
                  ...prev,
                  externalInstitutionReasons: v,
                }))
              }
              clearable
            />
            {hasReportIncludedFilter && (
              <Select
                label="Report Included"
                placeholder="Any"
                data={REPORT_INCLUDED_OPTIONS}
                value={
                  pendingFilters.reportIncluded === null
                    ? null
                    : pendingFilters.reportIncluded
                    ? "yes"
                    : "no"
                }
                onChange={(v) =>
                  setPendingFilters((prev) => ({
                    ...prev,
                    reportIncluded: v === null ? null : v === "yes",
                  }))
                }
                clearable
              />
            )}
            {hasTrackingNumberFilter && (
              <Select
                label="Tracking Number"
                placeholder="Any"
                data={TRACKING_NUMBER_OPTIONS}
                value={
                  pendingFilters.hasTrackingNumber === null
                    ? null
                    : pendingFilters.hasTrackingNumber
                    ? "yes"
                    : "no"
                }
                onChange={(v) =>
                  setPendingFilters((prev) => ({
                    ...prev,
                    hasTrackingNumber: v === null ? null : v === "yes",
                  }))
                }
                clearable
              />
            )}
            {hasReturnStatusFilter && (
              <MultiSelect
                label="Return Status"
                placeholder="All statuses"
                data={RETURN_STATUS_OPTIONS}
                value={pendingFilters.returnStatus}
                onChange={(v) =>
                  setPendingFilters((prev) => ({ ...prev, returnStatus: v }))
                }
                clearable
              />
            )}
            {hasPendingReviewFilter && (
              <Checkbox
                label="Pending review only"
                checked={pendingFilters.pendingReviewOnly}
                onChange={(e) =>
                  setPendingFilters((prev) => ({
                    ...prev,
                    pendingReviewOnly: e.currentTarget.checked,
                  }))
                }
              />
            )}
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="default" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* ===== MODALS ===== */}
        <Modal
          opened={Boolean(tissueCypherToShip)}
          onClose={handleTissueCypherShipClose}
          title="Confirm Castle shipment"
          centered
        >
          <Stack>
            <Text>
              Mark case {tissueCypherToShip?.case?.caseNumber ?? "N/A"} as
              shipped to Castle Biosciences?
            </Text>
            <Group justify="flex-end">
              <Button
                variant="default"
                h={44}
                onClick={handleTissueCypherShipClose}
              >
                Cancel
              </Button>
              <Button
                h={44}
                loading={markShippedMutation.isPending}
                onClick={handleTissueCypherShipConfirm}
              >
                Confirm shipment
              </Button>
            </Group>
          </Stack>
        </Modal>

        <SendOutApprovalModal
          opened={approvalModalOpen}
          onClose={closeApproval}
          sendOutId={selectedSendOutId}
          onSuccess={() => {
            closeApproval();
            handleRefreshAll();
          }}
        />

        <SendOutDetailModal
          opened={detailModalOpen}
          onClose={closeDetail}
          sendOutId={selectedSendOutId}
          onUpdate={() => {
            handleRefreshAll();
          }}
        />

        <OverdueReportModal
          opened={overdueReportOpen}
          onClose={closeOverdueReport}
        />

        <LogNewSendOutModal
          key={resubmitSendOut?.id ?? "new"}
          opened={logNewOpen}
          onClose={handleLogNewClose}
          existingSendOut={resubmitSendOut}
          onSuccess={() => {
            handleLogNewClose();
            handleRefreshAll();
          }}
        />
      </Box>
    </Container>
  );
}
